import { useCallback } from 'react';

import { useOnConfirm } from '../contexts/DialogContext';
import { openInNewTab } from '../utils';

/**
 * Custom hook for navigating to external URLs with a confirmation dialog.
 *
 * @param url - The external URL to navigate to. If null or undefined, the returned function will be a no-op.
 * @returns A callback function that shows a confirmation dialog and opens the URL in a new tab.
 *
 * @example
 * ```tsx
 * const Component = () => {
 *   const handleNavigate = useExternalNavigation('https://github.com/example');
 *
 *   return <button onClick={handleNavigate}>Go to GitHub</button>;
 * };
 * ```
 */
export const useExternalNavigation = (url?: string | null): (() => void) => {
  const onConfirm = useOnConfirm();

  return useCallback(() => {
    if (!url) {
      return;
    }

    onConfirm({
      title: 'Leaving Fossa',
      body: (
        <div className="modal-link-navigate-text">
          By clicking this link, you will be navigated away from FOSSA to <b>{url}</b>
        </div>
      ),
      onConfirm: () => openInNewTab(url),
    });
  }, [url, onConfirm]);
};
